/**
 * Role tier label for the current user, colour-coded by privilege:
 *   admin  → memory  (highest privilege — visually distinct)
 *   write  → now     (operator tier)
 *   read   → success (viewer tier)
 *   null   → muted   (no role assigned — principal authenticated but ungated)
 *
 * Rendered as plain inline text so the colour alone carries the signal — no
 * pill chrome competing with the surrounding UI.
 *
 * `appearance="muted"` drops the privilege-tier colour for a flat caption, for
 * callers (the account disclosure) where the role sits alongside other
 * identity text and should not compete for attention.
 */

import React from 'react'
import type { UserRole } from '@/types/auth'
import { ROLE_LABELS } from '@/utils/roleDisplay'

interface RoleBadgeProps {
  role: UserRole | null
  appearance?: 'default' | 'muted'
}

const ROLE_STYLES: Record<NonNullable<UserRole>, string> = {
  admin: 'text-ui-memory',
  write: 'text-ui-now',
  read: 'text-ui-success',
}

const NO_ACCESS_STYLES = 'text-ui-text-faint'

const RoleBadge: React.FC<RoleBadgeProps> = ({
  role,
  appearance = 'default',
}) => {
  const muted = appearance === 'muted'
  if (role === null) {
    return (
      <span
        className={`inline-flex items-center text-meta ${muted ? 'text-ui-text-muted' : NO_ACCESS_STYLES}`}
        aria-label="No role assigned"
      >
        No access
      </span>
    )
  }

  return (
    <span
      className={`inline-flex items-center text-meta ${muted ? 'text-ui-text-muted' : ROLE_STYLES[role]}`}
      aria-label={`Role: ${ROLE_LABELS[role]}`}
    >
      {ROLE_LABELS[role]}
    </span>
  )
}

RoleBadge.displayName = 'RoleBadge'

export default RoleBadge
